/**
 * Gestão de consentimento de cookies (LGPD) + Google Consent Mode v2.
 *
 * Os defaults ("denied") são declarados inline no index.html, ANTES de qualquer
 * tag. Este módulo lê a preferência salva, envia o `consent update` e só então
 * carrega as tags que dependem de consentimento (GA4, Google Ads, pixel OpenAI).
 */

export const CONSENT_STORAGE_KEY = "topclass_cookie_consent";

export type ConsentDecision = "accepted" | "rejected" | "custom";

export interface ConsentState {
  decision: ConsentDecision;
  analytics: boolean;
  marketing: boolean;
  /** ISO date do registro da escolha */
  date: string;
}

type Listener = (state: ConsentState | null) => void;

const listeners = new Set<Listener>();

/** Lê a preferência salva (null = usuário ainda não decidiu). */
export function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (!parsed || typeof parsed.decision !== "string") return null;
    return {
      decision: parsed.decision as ConsentDecision,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      date: parsed.date || new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

/* Consent Mode v2 removido a pedido do Google Ads (as metas de conversão não
   ativavam com o consent mode negando a comunicação). GA4, Google Ads e o pixel
   do ChatGPT Ads passam a carregar em todas as páginas. */
export function hasAnalyticsConsent() {
  return true;
}

export function hasMarketingConsent() {
  return true;
}



let openaiLoaded = false;

/** Carrega o pixel do ChatGPT Ads. */
function loadOpenAIPixel() {
  if (openaiLoaded || typeof window === "undefined") return;
  openaiLoaded = true;
  const q = ((...args: unknown[]) => {
    (q as unknown as { q: unknown[][] }).q.push(args);
  }) as unknown as (...args: unknown[]) => void;
  (q as unknown as { q: unknown[][] }).q = [];
  if (!window.oaiq) window.oaiq = q;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
  document.head.appendChild(script);
  window.oaiq?.("init", { pixelId: "6wUv9fxjqJWbn2uBEbcRtg" });
}

/**
 * Aplica o estado de consentimento: atualiza o Consent Mode e carrega as tags
 * permitidas. Import dinâmico evita puxar o analytics quando nada é permitido.
 */
export function applyConsent(_state?: ConsentState) {
  void import("./analytics").then((m) => m.initAnalytics());
  loadOpenAIPixel();
}

/** Salva a escolha, aplica e notifica a UI. */
export function saveConsent(input: {
  decision: ConsentDecision;
  analytics: boolean;
  marketing: boolean;
}) {
  const state: ConsentState = { ...input, date: new Date().toISOString() };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* localStorage indisponível: aplica só na sessão atual */
  }
  applyConsent(state);
  listeners.forEach((l) => l(state));
  return state;
}

/** Reaplica a escolha já registrada (chamado no boot da aplicação). */
export function initConsent() {
  const state = readConsent();
  applyConsent();
  return state;
}

export function onConsentChange(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Abre o painel de preferências de qualquer parte do site. */
export const OPEN_PREFERENCES_EVENT = "topclass:open-cookie-preferences";

export function openCookiePreferences() {
  window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT));
}
